import { useMemo } from 'react';
import { useComparison } from "@/hooks/use-comparisons";
import { useClaim } from "@/hooks/use-claim";
import {
    EnhancedRoomMatch,
    SummaryStats,
    HydratedMatchGroup,
    OrganizedRoom
} from "@/components/comparison/types";
import { ComparisonFullDetails } from "@/api/generated/client/models/ComparisonFullDetails";
import { LineItemWithMatchInfo } from "@/api/generated/client/models/LineItemWithMatchInfo";
import { RoomDetail } from "@/api/generated/client/models/RoomDetail";
import { LineItemMatchGroupWithItems } from "@/api/generated/client/models/LineItemMatchGroupWithItems";

export interface ComparisonData {
    stats: SummaryStats;
    enhancedMatches: EnhancedRoomMatch[];
    hydratedMatchGroups: HydratedMatchGroup[];
    organizedRooms: OrganizedRoom[];
    allRooms: RoomDetail[];
    unmatchedLineItemsA: (LineItemWithMatchInfo & { rcv: number; room: string })[];
    unmatchedLineItemsB: (LineItemWithMatchInfo & { rcv: number; room: string })[];
    unmatchedInsurerRooms: OrganizedRoom[];
    unmatchedContractorRooms: OrganizedRoom[];
}

export function useComparisonData(claimId: number, comparisonId: number) {
    const { data: comparisonData, isLoading: isComparisonLoading, isProcessing } = useComparison(comparisonId);
    const { claim, isLoading: isClaimLoading } = useClaim(claimId);

    const isLoading = isComparisonLoading || isClaimLoading;

    // Transform API data to component data
    const transformedData = useMemo<ComparisonData | null>(() => {
        if (!comparisonData) return null;

        // The API returns a normalized structure, not fully nested
        // Using mock data as requested
        const response = comparisonData as unknown as ComparisonFullDetails;
        // const response = comparisonData as unknown as ComparisonFullDetails;
        const roomMatchGroups = response.roomMatchGroups || [];

        // 1. Create Lookup Maps
        const insurerItemsMap = new Map<number, LineItemWithMatchInfo>((response.insurerLineItems || []).map((i) => [i.lineItemId, i]));
        const contractorItemsMap = new Map<number, LineItemWithMatchInfo>((response.contractorLineItems || []).map((i) => [i.lineItemId, i]));
        const insurerRoomsMap = new Map<number, RoomDetail>((response.insurerRooms || []).map((r) => [r.id, r]));
        const contractorRoomsMap = new Map<number, RoomDetail>((response.contractorRooms || []).map((r) => [r.id, r]));

        // 2. Calculate Totals (RCV)
        const totalInsuranceRCV = (response.insurerLineItems || []).reduce((sum, item) => sum + (item.rcvCents || 0), 0) / 100;
        const totalContractorRCV = (response.contractorLineItems || []).reduce((sum, item) => sum + (item.rcvCents || 0), 0) / 100;
        const totalDifference = totalContractorRCV - totalInsuranceRCV;
        const percentDifference = totalInsuranceRCV > 0 ? (totalDifference / totalInsuranceRCV) * 100 : 0;

        // 3. Process Matches
        let insuranceOverscope = 0;
        let overscopeItemCount = 0;

        type UnmatchedItem = LineItemWithMatchInfo & { rcv: number; room: string };

        interface OverscopeItem {
            room: string;
            insuranceItem: LineItemWithMatchInfo | undefined;
            contractorItem: LineItemWithMatchInfo | undefined;
            difference: number;
        }

        const overscopeItems: OverscopeItem[] = [];

        let contractorOverscope = 0;
        let contractorOverscopeItemCount = 0;
        const contractorOverscopeItems: OverscopeItem[] = [];

        // Track unmatched items
        const unmatchedLineItemsA: UnmatchedItem[] = []; // Carrier Pre-Approved (Insurer Only)
        const unmatchedLineItemsB: UnmatchedItem[] = []; // Omitted (Contractor Only)

        // Find Unmatched items directly from the lists if they have no lineItemMatchGroupId
        (response.insurerLineItems || []).forEach((item) => {
            if (!item.lineItemMatchGroupId) {
                const room = item.roomId ? insurerRoomsMap.get(item.roomId) : undefined;
                const roomName = room ? (room.level ? `${room.level} - ${room.name}` : room.name) : "Unknown";
                unmatchedLineItemsA.push({
                    ...item,
                    rcv: (item.rcvCents || 0) / 100,
                    room: roomName
                });
            }
        });

        (response.contractorLineItems || []).forEach((item) => {
            if (!item.lineItemMatchGroupId) {
                const room = item.roomId ? contractorRoomsMap.get(item.roomId) : undefined;
                const roomName = room ? (room.level ? `${room.level} - ${room.name}` : room.name) : "Unknown";
                unmatchedLineItemsB.push({
                    ...item,
                    rcv: (item.rcvCents || 0) / 100,
                    room: roomName
                });
            }
        });

        const lineItemMatchGroups = response.lineItemMatchGroups || [];

        // Helper to process difference
        const processMatchGroup = (group: LineItemMatchGroupWithItems): HydratedMatchGroup | null => {
            // Resolve item IDs to objects
            const insurerItems = (group.insurerLineItems || [])
                .map((id) => insurerItemsMap.get(id))
                .filter((item): item is LineItemWithMatchInfo => !!item);
            const contractorItems = (group.contractorLineItems || [])
                .map((id) => contractorItemsMap.get(id))
                .filter((item): item is LineItemWithMatchInfo => !!item);

            return {
                id: (group.id || 0),
                insurerItems,
                contractorItems,
                confidence: group.confidence || 0,
                status: ((group.status as string) === 'user_confirmed' || (group.status as string) === 'approved') ? 'approved' :
                    (group.status as string) === 'rejected' ? 'rejected' : 'pending'
            };
        };

        const hydratedMatchGroups: HydratedMatchGroup[] = lineItemMatchGroups
            .map(processMatchGroup)
            .filter((g): g is HydratedMatchGroup => !!g);

        // Process Match Logic for Stats (Re-using the loop logic)
        hydratedMatchGroups.forEach(group => {
            const insurerTotal = group.insurerItems.reduce((sum, item) => sum + (item.rcvCents || 0), 0) / 100;
            const contractorTotal = group.contractorItems.reduce((sum, item) => sum + (item.rcvCents || 0), 0) / 100;
            const diff = contractorTotal - insurerTotal;

            // Resolve Room Name (Simple heuristic: first item's room)
            let roomName = "Unknown";
            if (group.insurerItems.length > 0 && group.insurerItems[0].roomId) {
                const r = insurerRoomsMap.get(group.insurerItems[0].roomId);
                if (r) roomName = r.level ? `${r.level} - ${r.name}` : r.name;
            } else if (group.contractorItems.length > 0 && group.contractorItems[0].roomId) {
                const r = contractorRoomsMap.get(group.contractorItems[0].roomId);
                if (r) roomName = r.level ? `${r.level} - ${r.name}` : r.name;
            }

            if (diff > 0) {
                // Contractor > Insurer -> Underscoped (Orange)
                contractorOverscope += diff;
                contractorOverscopeItemCount++;
                contractorOverscopeItems.push({
                    room: roomName,
                    insuranceItem: group.insurerItems[0],
                    contractorItem: group.contractorItems[0],
                    difference: diff
                });
            } else if (diff < 0) {
                // Insurer > Contractor -> Carrier Overscope (Green)
                const absDiff = Math.abs(diff);
                insuranceOverscope += absDiff;
                overscopeItemCount++;
                overscopeItems.push({
                    room: roomName,
                    insuranceItem: group.insurerItems[0],
                    contractorItem: group.contractorItems[0],
                    difference: absDiff
                });
            }
        });

        // 4.5 Construct Organized Rooms
        // Collect all rooms first
        const allRooms = [...Array.from(insurerRoomsMap.values()), ...Array.from(contractorRoomsMap.values())];
        // Deduplicate by ID
        const uniqueRoomsMap = new Map<number, RoomDetail>();
        allRooms.forEach(r => uniqueRoomsMap.set(r.id, r));
        const uniqueRooms = Array.from(uniqueRoomsMap.values());

        // Helper to check match status
        const getMatchIdForRoom = (id: number, side: 'insurer' | 'contractor') => {
            // Find which match group this room belongs to, if any
            const match = roomMatchGroups.find(g => {
                if (side === 'insurer') return (g.insurerRooms || []).includes(id);
                return (g.contractorRooms || []).includes(id);
            });
            return match?.id;
        };

        const organizedRooms: OrganizedRoom[] = uniqueRooms.map(room => {
            // Determine side based on which map it came from (or both)
            // Ideally a room id is unique to a side in this system context? 
            // The API models suggest separate lists. Let's assume ID uniqueness or strict checking.
            const isInsurer = insurerRoomsMap.has(room.id);
            const isContractor = contractorRoomsMap.has(room.id);

            // Default to insurer if both (shouldn't happen with correct data) or first found
            if (isInsurer && isContractor) {
                console.error('Room found in both insurer and contractor maps, this should not happen', {
                    roomId: room.id,
                    roomName: room.name
                });
            }
            const type: 'insurer' | 'contractor' = isInsurer ? 'insurer' : 'contractor';

            const items = [
                ...(response.insurerLineItems || []).filter(i => i.roomId === room.id),
                ...(response.contractorLineItems || []).filter(i => i.roomId === room.id)
            ];

            const totalRCV = items.reduce((sum, i) => sum + (i.rcvCents || 0), 0) / 100;
            const matchId = getMatchIdForRoom(room.id, type);

            return {
                id: room.id,
                name: room.name,
                level: room.level,
                items,
                totalRCV,
                type,
                matchId
            };
        });


        // 4. Construct Enhanced Matches (Room Matches) for display

        // Track which rooms correspond to a match to filter unmatched ones later
        const matchedInsurerRoomIds = new Set<number>();
        const matchedContractorRoomIds = new Set<number>();

        const enhancedMatches: EnhancedRoomMatch[] = roomMatchGroups.map((group) => {
            // Arrays of objects
            const iRooms = (group.insurerRooms || []).map((id) => {
                matchedInsurerRoomIds.add(id);
                return insurerRoomsMap.get(id);
            }).filter((r): r is RoomDetail => !!r);

            const cRooms = (group.contractorRooms || []).map((id) => {
                matchedContractorRoomIds.add(id);
                return contractorRoomsMap.get(id);
            }).filter((r): r is RoomDetail => !!r);

            // Construct Details
            const insurerDetails = iRooms.map(room => {
                const items = (response.insurerLineItems || []).filter(i => i.roomId === room.id);
                const total = items.reduce((s, i) => s + (i.rcvCents || 0), 0) / 100;
                return { room, items, total };
            });

            const contractorDetails = cRooms.map(room => {
                const items = (response.contractorLineItems || []).filter(i => i.roomId === room.id);
                const total = items.reduce((s, i) => s + (i.rcvCents || 0), 0) / 100;
                return { room, items, total };
            });

            const iTotal = insurerDetails.reduce((s, d) => s + d.total, 0);
            const cTotal = contractorDetails.reduce((s, d) => s + d.total, 0);
            const diff = cTotal - iTotal;
            const pct = iTotal > 0 ? (diff / iTotal) * 100 : 0;

            return {
                id: group.id || 0,
                roomA: iRooms.map((r) => r.level ? `${r.level} - ${r.name}` : r.name),
                roomB: cRooms.map((r) => r.level ? `${r.level} - ${r.name}` : r.name),
                insurerDetails,
                contractorDetails,
                confidence: group.confidence || 0,
                reasoning: group.note || undefined,
                rcvDifference: diff,
                percentDifference: pct,
                review: group.confirmationStatus ? {
                    status: group.confirmationStatus === 'confirmed' ? 'approved' :
                        group.confirmationStatus === 'rejected' ? 'rejected' : 'pending'
                } : undefined
            };
        });

        // Calculate Unmatched Rooms
        const unmatchedInsurerRooms: OrganizedRoom[] = Array.from(insurerRoomsMap.values())
            .filter(r => !matchedInsurerRoomIds.has(r.id))
            .map(room => {
                const items = (response.insurerLineItems || []).filter(i => i.roomId === room.id);
                const totalRCV = items.reduce((s, i) => s + (i.rcvCents || 0), 0) / 100;
                return { id: room.id, name: room.name, level: room.level, items, totalRCV, type: 'insurer' };
            });

        const unmatchedContractorRooms: OrganizedRoom[] = Array.from(contractorRoomsMap.values())
            .filter(r => !matchedContractorRoomIds.has(r.id))
            .map(room => {
                const items = (response.contractorLineItems || []).filter(i => i.roomId === room.id);
                const totalRCV = items.reduce((s, i) => s + (i.rcvCents || 0), 0) / 100;
                return { id: room.id, name: room.name, level: room.level, items, totalRCV, type: 'contractor' };
            });

        // 5. Final Stats
        const stats: SummaryStats = {
            totalInsuranceRCV,
            totalContractorRCV,
            totalDifference,
            percentDifference,

            insuranceOverscope,
            overscopeItemCount,
            overscopeItems,

            contractorOverscope,
            contractorOverscopeItemCount,
            contractorOverscopeItems,

            matchedCount: roomMatchGroups.length,
            approvedCount: 0,
            rejectedCount: 0,
            pendingCount: roomMatchGroups.length, // Assume all pending if not verified in API?

            highConfidenceCount: roomMatchGroups.filter((m) => (m.confidence || 0) > 0.9).length,
            mediumConfidenceCount: roomMatchGroups.filter((m) => (m.confidence || 0) > 0.7 && (m.confidence || 0) <= 0.9).length,
            lowConfidenceCount: roomMatchGroups.filter((m) => (m.confidence || 0) <= 0.7).length,

            unmatchedInsuranceCount: unmatchedLineItemsA.length,
            unmatchedContractorCount: unmatchedLineItemsB.length,
            unmatchedInsuranceRCV: unmatchedLineItemsA.reduce((sum, item) => sum + item.rcv, 0),
            unmatchedContractorRCV: unmatchedLineItemsB.reduce((sum, item) => sum + item.rcv, 0),
            reviewProgress: 0,
        };

        return {
            stats,
            enhancedMatches,
            hydratedMatchGroups,
            organizedRooms,
            allRooms: uniqueRooms,
            unmatchedLineItemsA,
            unmatchedLineItemsB,
            unmatchedInsurerRooms,
            unmatchedContractorRooms
        };

    }, [comparisonData]);

    return {
        comparisonData,
        transformedData,
        isLoading,
        isProcessing,
        claim
    };
}
